// Minimal layout for the health area of the portal. Deliberately NOT the
// parishes Layout/frame: external facilitators are scoped to the health area
// only (handoff Section 4, nav isolation), so this layout renders no parish
// navigation at all, just the shared PortalHeader and a small health sub-nav.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { Link, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { PortalHeader, SignOutMenu } from '@cca/ui';
import { signOutAllDevices, signOutThisDevice } from '../../frame/signout';
import { healthApi, HealthApiError, type CyclesResponse } from './api';
import './health.css';

export interface HealthFrameContext {
  role: CyclesResponse['role'];
  cycles: CyclesResponse['cycles'];
  reloadCycles: () => void;
}

export function useHealthFrame(): HealthFrameContext {
  return useOutletContext<HealthFrameContext>();
}

export function HealthLayout(): JSX.Element {
  const [data, setData] = useState<CyclesResponse | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const load = useCallback(() => {
    healthApi
      .cycles()
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof HealthApiError && err.status === 401) {
          navigate(`/auth/login?returnTo=${encodeURIComponent(pathname)}`, { replace: true });
          return;
        }
        if (err instanceof HealthApiError && err.status === 403) {
          setNoAccess(true);
          return;
        }
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => setLoading(false));
    // navigate/pathname are stable enough for this once-per-mount load.
  }, [navigate, pathname]);

  useEffect(() => {
    load();
  }, [load]);

  const isAdmin = data?.role === 'admin';
  const onInstrument = pathname.startsWith('/portal/health/admin/instrument');
  const onOverview = pathname.startsWith('/portal/health/admin/overview');
  const onSettings = pathname.startsWith('/portal/health/admin') && !onInstrument && !onOverview;
  const onGuide = pathname.startsWith('/portal/health/guide');

  const subNav = (
    <nav
      aria-label="Church Health Assessment"
      style={{ background: '#fff', borderBottom: '1px solid #e5e0d5' }}
    >
      {/* Inner row centred to the same column as <main> below, so the nav tabs
          line up with the page content (and the per-cycle sub-tabs) rather than
          sitting flush against the full-width header. */}
      <div
        style={{
          maxWidth: 1060,
          margin: '0 auto',
          padding: '0 20px',
          display: 'flex',
          gap: 4,
          // The tabs (with the right-pushed Guide) exceed a phone width; scroll
          // them within the bar rather than widening the whole page.
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <SubNavLink
          to="/portal/health"
          active={!onSettings && !onInstrument && !onGuide && !onOverview}
        >
          Assessments
        </SubNavLink>
        {isAdmin && (
          <SubNavLink to="/portal/health/admin/overview" active={onOverview}>
            Overview
          </SubNavLink>
        )}
        {isAdmin && (
          <SubNavLink to="/portal/health/admin/instrument" active={onInstrument}>
            Instrument
          </SubNavLink>
        )}
        {isAdmin && (
          <SubNavLink to="/portal/health/admin/settings" active={onSettings}>
            Settings
          </SubNavLink>
        )}
        {isAdmin && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
            <SubNavLink to="/portal/health/guide" active={onGuide}>
              Guide
            </SubNavLink>
          </span>
        )}
      </div>
    </nav>
  );

  return (
    <div
      className="health-scope"
      style={{
        minHeight: '100vh',
        background: 'var(--health-paper, #fdfbf6)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PortalHeader
        portalName="Church Health Assessment"
        rightSlot={<SignOutMenu onSignOut={signOutThisDevice} onSignOutAll={signOutAllDevices} />}
        subNav={subNav}
      />
      <main
        style={{
          width: '100%',
          maxWidth: 1060,
          margin: '0 auto',
          padding: '26px 20px 56px',
          flex: 1,
        }}
      >
        {loading && <p style={{ color: 'var(--health-grey)' }}>Loading...</p>}
        {!loading && noAccess && (
          <div className="card" style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: 19, marginBottom: 10 }}>
              This area is not open to your account
            </h2>
            <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>
              The Church Health Assessment area is available to appointed facilitators, church
              leaders, and the Registrar. If you believe you should have access, please contact the
              Registrar at registrar@confessinganglicans.nz.
            </p>
          </div>
        )}
        {!loading && error && !noAccess && (
          <div className="card" style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: 19, marginBottom: 10 }}>Something went wrong</h2>
            <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>{error}</p>
          </div>
        )}
        {!loading && data && (
          <Outlet
            context={
              {
                role: data.role,
                cycles: data.cycles,
                reloadCycles: load,
              } satisfies HealthFrameContext
            }
          />
        )}
      </main>
      <footer
        style={{
          borderTop: '1px solid #e5e0d5',
          background: '#fff',
          padding: '14px 0',
          textAlign: 'center',
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--health-grey, #6b7280)',
        }}
      >
        CCAANZ Church Health Assessment · {new Date().getFullYear()}
      </footer>
    </div>
  );
}

function SubNavLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '13px 16px',
        fontSize: 13.5,
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--health-accent, #5c7e25)' : 'var(--health-grey, #6b7280)',
        borderBottom: active ? '2px solid var(--health-accent, #5c7e25)' : '2px solid transparent',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        flex: '0 0 auto',
      }}
    >
      {children}
    </Link>
  );
}
